import React from 'react';
import { useTranslation } from 'react-i18next';
import '../styles/PracticeReportModal.css';

const formatDifficultyLevel = (level) => {
  if (!level) return 'Unknown';
  if (level === 'initial_test') return 'Initial Test';
  if (level.startsWith('initial_test_')) {
    const parts = level.split('_');
    if (parts.length >= 3) {
      return `Initial Test (${parts[2]}/20)`;
    }
    return 'Initial Test';
  }
  return level.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

const PracticeReportModal = ({ report, isOpen, onClose }) => {
  const { t } = useTranslation();

  // ✨ 1. 移除 'if (!isOpen) return null;' 判斷
  // 我們現在使用 CSS class 來控制可見性，以支持過渡動畫。

  // ✨ 2. 在解構時為 report 提供一個默認的空對象，防止 isOpen 為 true 但 report 尚未加載時出錯。
  const {
    overall_assessment = '',
    detailed_error_analysis = [],
    suggested_practice_focus = [],
    practice_sentence = '',
    executive_summary = '',
    metadata = {}
  } = report || {}; // <-- ✨ 添加 || {}

  return (
    // ✨ 3. 在頂層 div 上，根據 isOpen 和 report 的狀態動態添加 'visible' class
    <div 
      className={`report-modal-overlay ${isOpen && report ? 'visible' : ''}`} 
      onClick={onClose}
    >
      {/* ✨ 4. 添加一個條件渲染，確保只有在 report 存在時才渲染 modal 的內容 */}
      {report && (
        <div className="report-modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="report-modal-header">
            <h3>{t('reportModal.title') || 'Pronunciation Analysis Report'}</h3>
            <button onClick={onClose} className="report-modal-close-btn">&times;</button>
          </div>
          
          <div className="report-modal-body">
            <div className="report-section">
              <h4>{t('reportModal.executiveSummary') || 'Executive Summary'}</h4>
              <p className="summary-text">{executive_summary}</p>
            </div>

            <div className="report-section">
              <h4>{t('reportModal.overallAssessment') || 'Overall Assessment'}</h4>
              <p>{overall_assessment}</p>
            </div>

            <div className="report-section">
              <h4>{t('reportModal.detailedErrorAnalysis') || 'Detailed Error Analysis'}</h4>
              {detailed_error_analysis.length > 0 ? (
                <ul className="error-list">
                  {detailed_error_analysis.map((error, index) => (
                    <li key={index}>
                      <span className="error-sound">/{error.sound_in_ipa}/</span>
                      <span className="error-word">({error.word})</span>
                      <p className="error-description">{error.analysis}</p>
                    </li>
                  ))}
                </ul>
              ) : (
                <p>{t('reportModal.noErrors') || 'No specific errors were identified in this practice.'}</p>
              )}
            </div>

            <div className="report-section">
              <h4>{t('reportModal.suggestedPracticeFocus') || 'Suggested Practice Focus'}</h4>
              {suggested_practice_focus.length > 0 ? (
                <ul className="focus-list">
                  {suggested_practice_focus.map((focus, index) => (
                    <li key={index}>
                      <span className="focus-sound">/{focus.sound_in_ipa}/</span>
                      <p className="focus-reason">{focus.reason}</p>
                    </li>
                  ))}
                </ul>
              ) : (
                <p>{t('reportModal.noSuggestions') || 'No specific practice suggestions at this time. Keep up the good work!'}</p>
              )}
            </div>

            {practice_sentence && (
              <div className="report-section practice-sentence-section">
                <h4>{t('reportModal.practiceSentence') || 'Suggested Practice Sentence'}</h4>
                <p className="practice-sentence-text">"{practice_sentence}"</p>
              </div>
            )}
          </div>

          <div className="report-modal-footer">
              <p>{t('reportModal.metadata', { 
                  sentence: metadata?.sentence, 
                  difficulty: formatDifficultyLevel(metadata?.difficulty_level), 
                  standard: metadata?.standard 
                  }) || `Analyzed sentence: "${metadata?.sentence}" at ${formatDifficultyLevel(metadata?.difficulty_level)} level.`}
              </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PracticeReportModal;
